import * as React from "react";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {ActiveUsersResponse, ActiveUsersStats} from "../users-dashboard/components/ActiveUsers";
import {AccessesListElement} from "../users-dashboard/components/UsersAccessesByHierarchy";
import {GREEN, RED} from "../UserManagement";
import {UserListContext} from "./UserListContext";
import {uuid} from "../../types/veboxModels";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {UsersAccessPerADay} from "../../types/UsersManagementTypes";

declare var AppTranslations: AppTranslations;

export enum DATA_STATE {
    isLoading = "isLoading",
    loaded = "loaded",
    error = "error",
}

export type WidgetsDashboardState = {
    widgetsList: uuid[],
    state: DATA_STATE
}

export type UsersDashboardStats = {
    activeUsersState: ActiveUsersState,
    usersAccessesByDayState: UsersAccessesByDayState,
    usersAccessesByHierarchyState: UsersAccessesByHierarchyState,
    widgetsDashboardState: WidgetsDashboardState,
    loadDashboardData: () => void,
    isFistRender: boolean
}

export type ActiveUsersState = {
    usersStats: ActiveUsersStats,
    state: DATA_STATE
}


export type UsersAccessesByDayState = {
    userAccessesByDayData: UsersAccessPerADay,
    state: DATA_STATE
}

export type UsersAccessesByHierarchyState = {
    userAccessesByHierarchyData: AccessesListElement[]
    state: DATA_STATE
}


const createActiveUsersStats = (data: ActiveUsersResponse): ActiveUsersStats => {
    return {
        items: [
            {
                color: GREEN,
                label: AppTranslations.usersManagement.activeUsers,
                frequency: data.numberOfActiveUser,
                code: AppTranslations.usersManagement.activeUsers.toLowerCase()
            },
            {
                color: RED,
                label: AppTranslations.usersManagement.blockedUsers,
                frequency: data.numberOfInactiveUser,
                code: AppTranslations.usersManagement.blockedUsers.toLowerCase()
            }
        ],
        total: data.numberOfInactiveUser + data.numberOfActiveUser
    }
}

const UsersDashboardContext = React.createContext<UsersDashboardStats>({} as any);

export const UsersDashboardProvider: React.FC = ({children}) => {

    const [isFistRender, setIsFistRender] = React.useState<boolean>(true);

    const [activeUsersState, setUsersStats] = React.useState<ActiveUsersState>({
        usersStats: undefined,
        state: DATA_STATE.isLoading
    });
    const [usersAccessesByDayState, setAccessesByDayData] = React.useState<UsersAccessesByDayState>({
        userAccessesByDayData: undefined,
        state: DATA_STATE.isLoading
    });
    const [usersAccessesByHierarchyState, setUserAccessesByHierarchyData] = React.useState<UsersAccessesByHierarchyState>(
        {
            userAccessesByHierarchyData: undefined,
            state: DATA_STATE.isLoading
        });

    const [widgetsDashboardState, setWidgetsDashboardState] = React.useState<WidgetsDashboardState>({
        widgetsList: undefined,
        state: DATA_STATE.isLoading
    });

    const {filterParams, stringFilter, filtersUpdated, filterGuid} = React.useContext(UserListContext);

    const setLoadingStatus = () => {
        setUsersStats({
            usersStats: undefined,
            state: DATA_STATE.isLoading
        })
        setAccessesByDayData({
            userAccessesByDayData: undefined,
            state: DATA_STATE.isLoading
        })
        setUserAccessesByHierarchyData({
            userAccessesByHierarchyData: undefined,
            state: DATA_STATE.isLoading
        })
    }

    const updateUsersStatus = () => {
        getUsersStatus(filterGuid ? {filterGuid} : filterParams, filterGuid ? "" : stringFilter)
            .then((data: ActiveUsersResponse) => {
                setUsersStats({
                    usersStats: createActiveUsersStats(data),
                    state: DATA_STATE.loaded
                });
            })
            .catch((err) => {
                setUsersStats({
                    usersStats: undefined,
                    state: DATA_STATE.error
                });
                console.error(err);
            })
    }
    const updateUsersAccessesByDay = () => {
        getUsersAccessesByDay(filterGuid ? {filterGuid} : filterParams, filterGuid ? "" : stringFilter)
            .then((data) => {
                setAccessesByDayData({
                    userAccessesByDayData: data,
                    state: DATA_STATE.loaded
                });
            })
            .catch((err) => {
                setAccessesByDayData({
                    userAccessesByDayData: undefined,
                    state: DATA_STATE.error
                });
                console.error(err)
            })
    }
    const updateUsersAccessesByHierarchy = () => {
        getUsersAccessesByHierarchy(filterGuid ? {filterGuid} : filterParams, filterGuid ? "" : stringFilter)
            .then((data: AccessesListElement[]) => {
                setUserAccessesByHierarchyData({
                    userAccessesByHierarchyData: data,
                    state: DATA_STATE.loaded
                });
            })
            .catch((err) => {
                setUserAccessesByHierarchyData({
                    userAccessesByHierarchyData: undefined,
                    state: DATA_STATE.error
                })
                console.error(err)
            })
    }

    const getDashboardChartsList = () => {
        getDashboardCharts()
            .then((list) => {
                setWidgetsDashboardState({
                    widgetsList: list,
                    state: DATA_STATE.loaded
                })
            })
            .catch((err) => {
                console.error(err)
                setWidgetsDashboardState({
                    widgetsList: undefined,
                    state: DATA_STATE.error
                })
            })
    }

    const loadDashboardData = () => {
        setLoadingStatus()
        updateUsersStatus();
        updateUsersAccessesByDay();
        updateUsersAccessesByHierarchy();
        setIsFistRender(false);
    }

    React.useEffect(() => {
        if ((!isFistRender && filtersUpdated) || filterGuid) {
            loadDashboardData();
        }
    }, [filterParams, filterGuid])

    React.useEffect(() => {
        getDashboardChartsList();
        loadDashboardData();
    }, [])


    return (
        <UsersDashboardContext.Provider
            value={{
                activeUsersState,
                usersAccessesByDayState,
                usersAccessesByHierarchyState,
                widgetsDashboardState,
                loadDashboardData,
                isFistRender
            }}
        >
            {children}
        </UsersDashboardContext.Provider>
    )
}

export function useUserDashboardData() {
    return React.useContext(UsersDashboardContext);
}

function getUsersStatus(filterParams, stringFilter: string) {
    return new Promise<ActiveUsersResponse>((res, rej) => {
        RestDataLoader.loadData(
            `statistics/users/activitiesCount?${stringFilter}`,
            filterParams,
            (data) => {
                return res(data);
            },
            rej,
        );
    })
}

export function getUsersAccessesByDay(filterParams, stringFilter: string) {
    return new Promise<UsersAccessPerADay>((res, rej) => {
        RestDataLoader.loadData(
            `statistics/users/activities?${stringFilter}`,
            filterParams,
            (data) => {
                return res(data);
            },
            rej,
        );
    })
}

function getUsersAccessesByHierarchy(filterParams, stringFilter: string) {
    return new Promise<AccessesListElement[]>((res, rej) => {
        RestDataLoader.loadData(
            `statistics/users/activitiesByCommercialHierarchies?${stringFilter}`,
            filterParams,
            ({list}) => {
                return res(list);
            },
            rej,
        );
    })
}

function getDashboardCharts() {
    return new Promise<uuid[]>((res, rej) => {
        RestDataLoader.loadData(
            `statistics/users/dashboardCharts`,
            {},
            ({list}) => {
                return res(list);
            },
            rej,
        );
    })
}